import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from '../data/supabase';
import type { OrderStageLookup } from '../lib/orderStages';

export function useOrderStageReference(supabase?: SupabaseClient) {
  const client = supabase ?? getSupabaseClient();
  const [lookup, setLookup] = React.useState<OrderStageLookup>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await client.from('event_status_reference').select('status,stage_index,label');
        if (!active) return;
        if (res.error) throw res.error;
        const data: { status?: string; stage_index?: number; label?: string }[] = res.data ?? [];
        const map: OrderStageLookup = {};
        for (const row of data) {
          const statusValue = row?.status;
          const status = typeof statusValue === 'string' ? statusValue : statusValue != null ? String(statusValue) : '';
          if (!status) continue;
          const stageIndexRaw = row?.stage_index;
          const stageIndex = typeof stageIndexRaw === 'number' ? stageIndexRaw : Number(stageIndexRaw ?? 0) || 0;
          const labelValue = row?.label;
          const label = typeof labelValue === 'string'
            ? (labelValue.trim() ? labelValue.trim() : null)
            : labelValue != null
              ? String(labelValue).trim() || null
              : null;
          map[status] = { stageIndex, label };
        }
        setLookup(map);
      } catch (err) {
        console.error('Failed to load order stage reference', err);
        if (active) {
          setError(err instanceof Error ? err.message : 'Failed to load order stage reference');
          setLookup({});
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [client]);

  return { lookup, loading, error };
}
